import React, { useContext, useMemo, useState } from "react";
import "../../css/header.scss";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import CartDrawer from "./CartDrawer";
import { useTranslation } from "react-i18next";
import { useSiteSettings } from "./SiteSettingsContext";
import i18n from "../../i18n";
import { languageFlagSrc, languageLabelKey } from "../../utils/languageDisplay";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { t } = useTranslation();
  const { openSettings } = useSiteSettings();
  const { items = [] } = useContext(CartContext) || {};
  const lang = i18n.language || "nl";
  const count = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.qty) || 0), 0),
    [items]
  );
  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`header${menuOpen ? " menu-open" : ""}`}
        role="navigation"
        aria-label="Main header"
      >
        <div className="header__logo" onClick={toTop} aria-label="Home">
          <svg width="0" height="0" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <filter id="logo-white-to-dark" colorInterpolationFilters="sRGB">
              <feColorMatrix 
                type="matrix" 
                values="1 0 -1.221 0 0.321
                        0 1 -1.221 0 0.321
                        0 0 -0.221 0 0.321
                        0 0 0 1 0" 
              />
            </filter>
          </svg>
          <Link to="/">
            <img alt="" src="/head/head-new.png"  />
          </Link>
        </div>

        <nav className="header__nav">
          <Link
            onClick={toTop}
            className={`header__link ${isActive("/about") ? "header__link--active" : ""
              }`}
            to="/about"
          >
            {t("header.about")}
          </Link>
          <span className="header__sep">|</span>
          <Link
            onClick={toTop}
            className={`header__link ${isActive("/shop") ? "header__link--active" : ""
              }`}
            to="/shop"
          >
            {t("header.shop")}
          </Link>
          <span className="header__sep">|</span>
          <Link
            onClick={toTop}
            className={`header__link ${isActive("/lab") ? "header__link--active" : ""
              }`}
            to="/lab"
          >
            {t("header.lab")}
          </Link>
          <span className="header__sep">|</span>
          <Link
            onClick={toTop}
            className={`header__link ${isActive("/calculator") ? "header__link--active" : ""
              }`}
            to="/calculator"
          >
            {t("header.calculator")}
          </Link>
          <span className="header__sep">|</span>
          <Link
            onClick={toTop}
            className={`header__link ${isActive("/help") ? "header__link--active" : ""
              }`}
            to="/help"
          >
            {t("header.help")}
          </Link>

          <hr className="header__menu-divider" aria-hidden="true" />
          <button
            type="button"
            className="header__lang-mobile"
            aria-label={t("header.langAria") || "Language & country"}
            aria-haspopup="dialog"
            onClick={() => {
              openSettings();
              setMenuOpen(false);
            }}
          >
            <img
              alt=""
              className="header__lang-mobile-flag"
              src={languageFlagSrc(lang)}
            />
            <img
              alt=""
              className="header__lang-mobile-chevron"
              src="/head/arrow1.svg"
            />
          </button>
        </nav>

        <div className="header__actions">
          <button
            className="icon-btn"
            aria-label={t("header.cartAria")}
            onClick={() => setShowDrawer(true)}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>

            <span className="badge">{count}</span>
          </button>
          <button
            className="icon-btn second-radius"
            aria-label={t("header.accountAria")}
            onClick={() => {
              window.location.href = "/account";
            }}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>

          </button>
          <button
            type="button"
            className="icon-btn header__lang-pill header__lang-pill--desktop"
            aria-label={t("header.langAria") || "Language"}
            aria-haspopup="dialog"
            onClick={openSettings}
          >
            <img
              alt=""
              className="header__lang-pill-flag"
              src={languageFlagSrc(lang)}
            />
            <span className="header__lang-pill-text">
              {t(languageLabelKey(lang))}
            </span>
            <img
              alt=""
              className="header__lang-pill-chevron"
              src="/head/arrow1.svg"
            />
          </button>

          <button
            className="icon-btn second-radius menu-toggle"
            aria-label={menuOpen ? "Close Menu" : "Open Menu"}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            ) : (
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            )}
          </button>
        </div>
      </header>
      {searchOpen && (
        <div className="searchModal" role="dialog" aria-modal="true">
          <button
            className="searchModal__close"
            aria-label="Close"
            onClick={() => setSearchOpen(false)}
          >
            ×
          </button>
          <div className="searchModal__container">
            <div className="searchModal__bar">
              <span className="searchModal__icon">
                <svg
                  t="1773631641106"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="7428"
                  width="200"
                  height="200"
                >
                  <path
                    d="M414.08 128v86.944c96.128 8.608 160.32 87.872 160.32 179.104a179.296 179.296 0 0 1-135.136 173.888L412.8 650.88l-3.68 11.072c47.296-3.072 94.592-19.936 135.744-48.192l16.32 24.544c45.472 16.576 80.512 52.864 96.192 87.584 15.68 35.328 16.224 53.696 71.808 94.848 55.616 40.864 91.904 71.008 100.8 74.08 9.216 3.072 29.216 1.216 47.968-17.504 18.688-18.784 20.48-38.752 17.408-47.68-3.072-8.896-33.152-45.12-74.304-100.768-41.152-55.616-59.936-56.224-94.944-71.872-35.008-15.68-71.264-50.4-87.552-95.872l-24.256-15.936c29.504-43.008 48.256-94.912 48.256-151.136 0-141.632-109.376-257.44-248.512-266.048z m-56.576 2.176C227.84 148.288 128 259.84 128 394.048c0 135.168 96.16 248.16 226.112 265.696v-89.376c-82.016-16.256-139.2-89.088-139.2-176.32a179.584 179.584 0 0 1 115.2-167.744l25.216-88.128 2.176-8z m26.496 15.936l-91.072 317.952 91.264-40.256v217.568L496.64 291.744 384 338.752v-192.64z m463.744 687.84c7.392 0 13.504 6.112 13.504 13.472a13.376 13.376 0 0 1-13.504 13.248 13.12 13.12 0 0 1-13.184-13.248c0-7.36 5.792-13.472 13.184-13.472z"
                    p-id="7429"
                  ></path>
                </svg>
              </span>
              <input
                className="searchModal__input"
                placeholder={t("header.searchPlaceholder")}
              />
            </div>
          </div>
        </div>
      )}
      <CartDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        items={items}
        image={"/main/main1.avif"}
        title={t("header.cartTitle")}
        options={[]}
        qty={0}
        price={0}
        subtotal={0}
      />
    </>
  );
};

export default Header;
